import type { QueryClient } from '@tanstack/react-query'
import { queryOptions as ts_queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { Address } from 'ox'
import { decodeEventLog, parseAbi } from 'viem'
import { Addresses } from 'viem/tempo'

import type * as WorkerAssets from '../../worker/lib/assets.js'
import * as Account from './account.js'
import { api } from './api.js'
import { getSupportedChainId } from './chain.js'
import * as Zone from './zone.js'

/** Returns the React Query key for wallet assets. */
export function queryKey(account: string | null | undefined, options: queryKey.Options = {}) {
  if (arguments.length === 1) return Account.queryKey(account, 'assets')
  const { chainId, fresh = false, tokens, zone = false, zones = false } = options
  return Account.queryKey(account, 'assets', chainId, { fresh, tokens, zone, zones })
}

/** `queryKey` types. */
export declare namespace queryKey {
  /** Query options. */
  type Options = {
    chainId?: number | undefined
    fresh?: boolean | undefined
    tokens?: string | undefined
    zone?: boolean | undefined
    zones?: boolean | undefined
  }
}

/** Fetches wallet assets through the shared assets API. */
export async function fetch(options: fetch.Options) {
  const { account, authToken, chainId, fresh, tokens, zones } = options
  const res = await api.api.assets.$get(
    {
      query: {
        address: account,
        chainId: String(chainId),
        ...(fresh && { fresh: 'true' }),
        ...(tokens?.length && { tokens: tokens.join(',') }),
        ...(zones && { zones: 'true' }),
      },
    },
    {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    },
  )
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    const error = new Error(body?.error ?? 'Failed to load assets') as Error & {
      status?: number | undefined
    }
    error.status = res.status
    throw error
  }
  return (await res.json()) as WorkerAssets.list.ReturnType
}

/** `fetch` types. */
export declare namespace fetch {
  /** Fetch options. */
  type Options = {
    /** Account address to fetch assets for. */
    account: Address.Address
    /** Optional zone auth token. */
    authToken?: string | undefined
    /** Parent Tempo chain id. */
    chainId: number
    /** Refresh balances with live reads. */
    fresh?: boolean | undefined
    /** Token addresses to force into the response. */
    tokens?: readonly string[] | undefined
    /** When `true`, fetches zone rows only. */
    zones?: boolean | undefined
  }
}

/** Returns React Query options for wallet assets. */
export function queryOptions(options: queryOptions.Options) {
  const { account, authToken, chainId, fresh, tokens, zones } = options
  const chainId_supported = getSupportedChainId(chainId)
  const tokenKey = getTokenKey(tokens)

  return ts_queryOptions({
    queryKey: queryKey(account, {
      chainId: chainId_supported,
      fresh: !!fresh,
      tokens: tokenKey,
      zone: !!authToken,
      zones: !!zones,
    }),
    queryFn: () =>
      fetch({
        account: account!,
        authToken: authToken ?? undefined,
        chainId: chainId_supported,
        fresh,
        tokens,
        zones,
      }),
  })
}

/** `queryOptions` types. */
export declare namespace queryOptions {
  /** Query options. */
  type Options = {
    /** Account address to fetch assets for. */
    account?: Address.Address | undefined
    /** Optional zone auth token. */
    authToken?: string | null | undefined
    /** Parent Tempo chain id. */
    chainId?: number | undefined
    /** Refresh balances with live reads. */
    fresh?: boolean | undefined
    /** Token addresses to force into the response. */
    tokens?: readonly string[] | undefined
    /** When `true`, fetches zone rows only. */
    zones?: boolean | undefined
  }
}

export function useAssets(options: useAssets.Options) {
  const { account, chainId, enabled = true, fresh, tokens, zones } = options

  const queryClient = useQueryClient()

  const zoneToken = Zone.useAuthToken()
  const authToken = zoneToken.data ?? null

  return useQuery({
    ...queryOptions({ account, authToken, chainId, fresh, tokens, zones }),
    enabled: enabled && Boolean(account && chainId),
    placeholderData: (previous, previousQuery) => {
      if (previous && previousQuery && account) {
        const [, prevAccount, prevType, prevChainId, prevScope] = previousQuery.queryKey as [
          unknown,
          unknown,
          unknown,
          unknown,
          { fresh?: unknown; tokens?: unknown; zone?: unknown; zones?: unknown } | undefined,
        ]
        if (
          prevType === 'assets' &&
          prevAccount &&
          Address.isEqual(prevAccount as Address.Address, account) &&
          prevChainId === chainId &&
          prevScope?.fresh === !!fresh &&
          prevScope?.tokens === getTokenKey(tokens) &&
          prevScope?.zone === !!authToken &&
          prevScope?.zones === !!zones
        )
          return previous
      }

      if (!account || !chainId) return undefined

      const matches = queryClient.getQueriesData<WorkerAssets.list.ReturnType>({
        queryKey: queryKey(account),
      })

      for (const [key, data] of matches) {
        if (!data || data.length === 0) continue

        const [, matchAccount, matchType, matchChainId, matchScope] = key as [
          unknown,
          unknown,
          unknown,
          unknown,
          { zones?: unknown } | undefined,
        ]

        if (
          matchType === 'assets' &&
          matchAccount &&
          Address.isEqual(matchAccount as Address.Address, account) &&
          matchChainId === chainId &&
          matchScope?.zones === !!zones
        )
          return data
      }

      return undefined
    },
    refetchInterval: 5_000,
  })
}

/** `useAssets` types. */
export declare namespace useAssets {
  /** Single asset returned by the worker assets API. */
  type Asset = WorkerAssets.list.Asset

  /** Hook options. */
  type Options = {
    account?: Address.Address | undefined
    /** Parent Tempo chain id. When the zone is unlocked the worker also fetches the zone chain decoded from the auth token. */
    chainId?: number | undefined
    enabled?: boolean | undefined
    /** Refresh every discovered token's balance with a live on-chain read (bypasses indexer lag). */
    fresh?: boolean | undefined
    /** Token addresses to fetch fresh on-chain balances for (bypasses indexer lag). */
    tokens?: readonly string[] | undefined
    /** When `true`, returns one row per (token, zone) holding (each row tagged with `zoneId`) and skips the parent-chain query. Rows in this mode have an empty `zones[]`. */
    zones?: boolean | undefined
  }
}

function getTokenKey(tokens: readonly string[] | undefined) {
  return tokens
    ?.map((token) => token.toLowerCase())
    .sort()
    .join(',')
}

const transferAbi = parseAbi([
  'event Transfer(address indexed from, address indexed to, uint256 value)',
] as const)

/**
 * Optimistically updates every cached `useAssets` result for `account`
 * by walking the Transfer logs of a confirmed receipt.
 *
 * Generic — handles regular sends/swaps/receives on the parent chain,
 * deposits into a zone, and withdrawals out of a zone. The strategy is
 * driven entirely by the receipt's `chainId` and the participating
 * counterparties:
 *
 * - Receipt on the **parent chain** → user's `from`/`to` deltas land on
 *   `asset.balance`. When `to` is the known zone portal address, the
 *   value is also mirrored as a credit on the matching `zones[]` entry
 *   (deposit), and the parent debit is preserved.
 * - Receipt on the **zone chain** → user's `from`/`to` deltas land on
 *   the zone's slim balance entry (or the row itself in `zonesOnly`
 *   mode). When the user is `from` the value is also mirrored as a
 *   credit on `asset.balance` (withdrawal: zone burn → parent mint).
 *
 * Never invalidates the assets query — the `useAssets` polling
 * interval reconciles indexer state. Calling `invalidateQueries` here
 * would race the indexer and stomp the optimistic delta.
 */
export function setReceiptData(options: setReceiptData.Options) {
  const { account, chainId, queryClient, receipt } = options
  const accountLower = account.toLowerCase()
  const isZone = chainId === Zone.chain.id
  const zoneId = isZone ? Zone.zoneId : (Zone.getActiveZoneId(queryClient) ?? Zone.zoneId)

  // Portal address used to detect "user → portal" Transfer events on
  // parent receipts (zone deposits). Wrapped in a try because parent
  // chains the wallet doesn't recognize will throw.
  const portalAddress = (() => {
    if (isZone) return null
    try {
      return Addresses.zonePortal(Zone.zoneId).toLowerCase()
    } catch {
      return null
    }
  })()

  // Per-token signed delta from the user's perspective on `chainId`.
  const deltas = new Map<string, bigint>()

  // Per-token amount that mirrors to the OTHER side (parent ↔ zone).
  const mirror = new Map<string, bigint>()

  for (const log of receipt.logs) {
    const decoded = (() => {
      try {
        return decodeEventLog({
          abi: transferAbi,
          data: log.data,
          topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]],
        })
      } catch {
        return null
      }
    })()
    if (!decoded || decoded.eventName !== 'Transfer') continue
    const { from, to, value } = decoded.args
    const fromUser = from.toLowerCase() === accountLower
    const toUser = to.toLowerCase() === accountLower
    if (!fromUser && !toUser) continue
    const token = log.address.toLowerCase()
    const delta = (toUser ? value : 0n) - (fromUser ? value : 0n)
    deltas.set(token, (deltas.get(token) ?? 0n) + delta)

    // Cross-side mirror: zone burn → parent mint, or parent → zone deposit.
    if (isZone && fromUser) mirror.set(token, (mirror.get(token) ?? 0n) + value)
    else if (!isZone && fromUser && portalAddress && to.toLowerCase() === portalAddress)
      mirror.set(token, (mirror.get(token) ?? 0n) + value)
  }
  if (deltas.size === 0) return

  queryClient.setQueriesData<WorkerAssets.list.ReturnType>(
    { queryKey: queryKey(accountLower) },
    (data) => {
      if (!data) return data
      return data.map((asset) => {
        const tokenLower = asset.address.toLowerCase()
        const delta = deltas.get(tokenLower) ?? 0n
        const mirrorAmount = mirror.get(tokenLower) ?? 0n
        if (delta === 0n && mirrorAmount === 0n) return asset

        if (isZone) {
          // Zone-side receipt: delta on the zone holding, mirror on parent.
          if (asset.zoneId === zoneId)
            return { ...asset, balance: (BigInt(asset.balance) + delta).toString() }
          if (asset.zoneId === null) {
            const zones = asset.zones.map((z) =>
              z.zoneId === zoneId ? { ...z, balance: (BigInt(z.balance) + delta).toString() } : z,
            )
            return {
              ...asset,
              balance: (BigInt(asset.balance) + mirrorAmount).toString(),
              zones,
            }
          }
          return asset
        }

        // Parent-chain receipt: delta on parent balance, mirror on the
        // active zone's slim entry. Don't touch zonesOnly rows since
        // the parent receipt has no first-hand zone movement.
        if (asset.zoneId === null) {
          const zones = asset.zones.map((z) =>
            z.zoneId === zoneId
              ? { ...z, balance: (BigInt(z.balance) + mirrorAmount).toString() }
              : z,
          )
          return {
            ...asset,
            balance: (BigInt(asset.balance) + delta).toString(),
            zones,
          }
        }
        return asset
      })
    },
  )
}

export declare namespace setReceiptData {
  /** Function options. */
  type Options = {
    /** Connected wallet address — used to find the user's Transfer leg in the receipt. */
    account: Address.Address
    /** Chain id the receipt is from. Determines whether to apply deltas to parent balances or to a zone entry. */
    chainId: number
    /** React Query client used to mutate every matching `useAssets` cache entry. */
    queryClient: QueryClient
    /** Confirmed transaction receipt whose logs include the user's Transfer. */
    receipt: { logs: readonly Log[] }
  }

  /** Minimal log shape consumed by the optimistic decoder. */
  type Log = {
    address: string
    data: `0x${string}`
    topics: readonly `0x${string}`[]
  }
}
